/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { CommonModule } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
    withXsrfConfiguration,
} from '@angular/common/http';
import {
    enableProdMode,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule, Title, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { DevExtremeModule, DxAccordionModule } from 'devextreme-angular';
import * as echarts from 'echarts';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { MomentModule } from 'ngx-moment';

// Ensure legacy tenant utils are loaded for global helper exposure
import { AppRoutingModule } from 'app/app-routing.module';
import { AppComponent, CustomInterceptor } from 'app/app.component';
import { ComponentModule } from 'app/core/components/components.module';
import { EventManagerService } from 'app/global-services/event-manager.service';
import {
    arraysEqual,
    eachFirstLetterToUpperCase,
} from 'app/shared/utils/shared-methods.utils';
import { provideEchartsCore } from 'ngx-echarts';
import { translocoConfig } from 'shared/core/config/transloco.config';
import { TenantConfigService } from 'shared/core/services/tenant/tenant-config.service';
import { environment } from 'shared/environments/environment';
import {
    EVENT_MANAGER_TOKEN,
    SHARED_METHODS_TOKEN,
} from 'shared/interfaces/shared-interfaces';

const runtime = (window as any).__env || {};

const env = {
    ...environment,
    ...runtime,
};

// env.js (runtime config) may be missing or stale in the browser cache —
// without an apiUrl fallback, requests go to "undefinedaccess/...".
if (!env.apiUrl) {
    env.apiUrl = '/api/';
}

if (env.name == 'prd') {
    console.log = () => {};
    console.debug = () => {};
    console.info = () => {};
    console.warn = () => {};
    enableProdMode();
} else {
    // Store original console methods **only in non-prod mode**
    const originalConsole = {
        log: console.log,
        debug: console.debug,
        info: console.info,
        warn: console.warn,
    };
    console.log('NOT PRODUCTION MODE ###########');
    // Restore console methods in development mode
    console.log = originalConsole.log;
    console.debug = originalConsole.debug;
    console.info = originalConsole.info;
    console.warn = originalConsole.warn;
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection(),
        // Preload tenant config before first render (avoids empty header/menu race)
        provideAppInitializer(() => inject(TenantConfigService).initialize()),
        importProvidersFrom(
            ComponentModule.forRoot(),
            CommonModule,
            BrowserModule,
            AppRoutingModule,
            DxAccordionModule,
            MomentModule,
            ModalModule.forRoot(),
            NgbModule,
            DevExtremeModule,
            ProgressbarModule.forRoot(),
            MatExpansionModule,
            MatSelectModule,
            MatFormFieldModule,
            MatGridListModule,
            MatToolbarModule,
            MatProgressSpinnerModule
        ),
        provideEchartsCore({ echarts }),
        Title,
        { provide: HTTP_INTERCEPTORS, useClass: CustomInterceptor, multi: true },
        { provide: EVENT_MANAGER_TOKEN, useClass: EventManagerService },
        {
            provide: SHARED_METHODS_TOKEN,
            useValue: { arraysEqual, eachFirstLetterToUpperCase },
        },
        provideAnimations(),
        // Features must be passed to a single provideHttpClient() call
        // Two separate calls do not merge — the last one wins, which previously dropped withInterceptorsFromDi() and silently disabled
        // customInterceptor (x-tenant-id / x-csrf-token request headers).
        provideHttpClient(
            withInterceptorsFromDi(),
            withXsrfConfiguration({
                cookieName: 'pulse.x-csrf-token',
                headerName: 'x-csrf-token',
            })
        ),
        translocoConfig,
        //provideExperimentalZonelessChangeDetection() // --> TODO the day we want to remove polyfills ...
    ],
}).catch(err => console.log(err));
